import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/hooks/useLanguage';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeText, sanitizeEmail, sanitizePhone, checkRateLimit, clearRateLimit } from '@/lib/security';

interface UseBookingSubmissionOptions {
  form: UseFormReturn<any>;
  setCurrentStep: (step: number) => void;
}

export const useBookingSubmission = ({ form, setCurrentStep }: UseBookingSubmissionOptions) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: any) => {
    // Rate limiting check
    if (!checkRateLimit('booking_form', 2, 600000)) {
      toast({
        title: language === 'es' ? 'Demasiados Intentos' : 'Too Many Attempts',
        description: language === 'es'
          ? 'Por favor espere unos minutos antes de intentar nuevamente.'
          : 'Please wait a few minutes before trying again.',
        variant: 'destructive',
      });
      return;
    }

    // Validate Zelle confirmation AND receipt before submission
    if (data.paymentMethod === 'Zelle') {
      const hasConfirmation = data.zelleConfirmation && data.zelleConfirmation.trim().length > 0;
      const hasReceipt = !!data.paymentEvidenceFile;

      if (!hasConfirmation || !hasReceipt) {
        toast({
          title: language === 'es' ? '⚠️ PROCESO INCOMPLETO' : '⚠️ INCOMPLETE PROCESS',
          description: language === 'es'
            ? 'Para continuar debe: 1) Completar el pago por Zelle, 2) Ingresar el número de confirmación, y 3) Subir el comprobante de pago.'
            : 'To continue you must: 1) Complete the Zelle payment, 2) Enter the confirmation number, and 3) Upload the payment receipt.',
          variant: 'destructive',
        });
        if (!hasConfirmation) {
          form.setError('zelleConfirmation', {
            type: 'manual',
            message: language === 'es'
              ? 'El número de confirmación de Zelle es obligatorio'
              : 'Zelle confirmation number is required',
          });
        }
        setCurrentStep(5);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const bookingId = `BK-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Get user IP for audit
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();

      // Combine date and time into appointment_start timestamp
      const appointmentDateTime = new Date(`${data.preferredDate}T${data.preferredTime}`);
      const appointmentEnd = new Date(appointmentDateTime.getTime() + 60 * 60 * 1000);

      // Upload Zelle receipt to storage if present
      let paymentEvidenceUrl = null;
      if (data.paymentMethod === 'Zelle' && data.paymentEvidenceFile) {
        const file = data.paymentEvidenceFile;
        const fileExt = file.name.split('.').pop();
        const fileName = `${bookingId}-receipt.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('payment-receipts')
          .upload(fileName, file, { cacheControl: '3600', upsert: false });

        if (uploadError) {
          console.error('Error uploading receipt:', uploadError);
        } else {
          paymentEvidenceUrl = fileName;
        }
      }

      const paymentStatus = data.paymentMethod === 'Zelle' ? 'pending_verification' : 'pending';

      const bookingData = {
        booking_id: bookingId,
        service: sanitizeText(data.service),
        full_name: sanitizeText(data.fullName),
        date_of_birth: data.dateOfBirth,
        email: sanitizeEmail(data.email),
        phone: sanitizePhone(data.phone),
        address: data.address ? sanitizeText(data.address) : null,
        employer_title: data.employerTitle ? sanitizeText(data.employerTitle) : null,
        situation: sanitizeText(data.situation),
        immigration_data: data.immigrationData || null,
        criminal_data: data.criminalData || null,
        injury_data: data.injuryData || null,
        family_data: data.familyData || null,
        tax_data: data.taxData || null,
        probate_data: data.probateData || null,
        real_estate_data: data.realEstateData || null,
        business_data: data.businessData || null,
        appointment_type: data.appointmentType,
        appointment_start: appointmentDateTime.toISOString(),
        appointment_end: appointmentEnd.toISOString(),
        referred_by: data.referredBy,
        referred_other: data.referredOther || null,
        payment_method: data.paymentMethod,
        payment_status: paymentStatus,
        payment_evidence_url: paymentEvidenceUrl,
        zelle_confirmation: data.paymentMethod === 'Zelle' ? data.zelleConfirmation : null,
        consent_notifications: data.consentNotifications,
        consent_non_refundable: data.consentTerms,
        consent_ip: ipData.ip,
        language: language,
      };

      // 1. Insert booking into database
      const { error } = await supabase.from('bookings').insert(bookingData);
      if (error) throw error;

      // 2. For non-Stripe payments, delegate all orchestration to the backend
      if (data.paymentMethod !== 'Stripe') {
        try {
          const { error: processError } = await supabase.functions.invoke('process-booking', {
            body: {
              bookingId,
              fullName: data.fullName,
              email: data.email,
              phone: data.phone,
              service: data.service,
              situation: data.situation,
              appointmentType: data.appointmentType,
              appointmentStart: appointmentDateTime.toISOString(),
              appointmentEnd: appointmentEnd.toISOString(),
              paymentMethod: data.paymentMethod,
              paymentStatus,
              language,
              zelleConfirmation: data.zelleConfirmation,
            },
          });

          if (processError) {
            console.error('[Booking] process-booking error:', processError);
            toast({
              title: language === 'es' ? 'Advertencia' : 'Warning',
              description: language === 'es'
                ? 'La reserva fue creada pero hubo un problema con las confirmaciones. Le contactaremos pronto.'
                : 'Booking was created but there was a problem with confirmations. We will contact you soon.',
              variant: 'destructive',
            });
          }
        } catch (processErr) {
          console.error('[Booking] process-booking exception:', processErr);
        }
      }

      // 3. Handle Stripe payment
      if (data.paymentMethod === 'Stripe') {
        const { data: sessionData, error: sessionError } = await supabase.functions.invoke(
          'create-booking-payment',
          {
            body: {
              bookingId,
              email: data.email,
              fullName: data.fullName,
              service: data.service,
              language,
            },
          }
        );
        if (sessionError) throw sessionError;
        if (sessionData?.url) {
          window.location.href = sessionData.url;
          return;
        }
      }

      // Clear saved form data
      localStorage.removeItem('bookingFormData');
      clearRateLimit('booking_form');

      const successMessage = getSuccessMessage(data, language);
      toast({ title: language === 'es' ? '¡Éxito!' : 'Success!', description: successMessage });

      if (data.paymentMethod !== 'Stripe') {
        form.reset();
        setCurrentStep(1);
      }
    } catch (error: any) {
      console.error('Booking submission error:', error instanceof Error ? error.message : 'Unknown error');
      toast({
        title: language === 'es' ? 'Error' : 'Error',
        description: language === 'es'
          ? 'Hubo un error al enviar su reserva. Por favor intente nuevamente.'
          : 'There was an error submitting your booking. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, onSubmit };
};

function getSuccessMessage(data: any, language: string): string {
  if (data.paymentMethod === 'Stripe') {
    return language === 'es' ? 'Redirigiendo al pago...' : 'Redirecting to payment...';
  }
  if (data.paymentMethod === 'Zelle') {
    return language === 'es'
      ? `Su reserva ha sido registrada con confirmación Zelle #${data.zelleConfirmation}. Verificaremos su pago y le enviaremos la confirmación de su cita.`
      : `Your booking has been registered with Zelle confirmation #${data.zelleConfirmation}. We will verify your payment and send you the appointment confirmation.`;
  }
  return language === 'es'
    ? 'Su reserva ha sido registrada. Recibirá un correo de confirmación una vez que procesemos su pago.'
    : 'Your booking has been registered. You will receive a confirmation email once we process your payment.';
}
